import React, { useMemo, useCallback } from 'react';
import { useIsMobile } from '../../../utils/useIsMobile';

/**
 * 캠페인 페이지네이션 컴포넌트
 * @param {Object} props
 * @param {number} props.page - 현재 페이지
 * @param {number} props.limit - 페이지 크기
 * @param {number} props.totalCount - 전체 건수
 * @param {Function} props.onPageChange - 페이지 변경 핸들러
 */
function CampaignPagination({ page = 1, limit = 50, totalCount = 0, onPageChange }) {
  const isMobile = useIsMobile();
  const totalPages = useMemo(() => Math.ceil(totalCount / limit), [totalCount, limit]);
  const startIndex = useMemo(() => (page - 1) * limit + 1, [page, limit]);
  const endIndex = useMemo(() => Math.min(page * limit, totalCount), [page, limit, totalCount]);

  const handlePrevPage = useCallback(() => {
    if (page > 1 && onPageChange) {
      onPageChange(page - 1);
    }
  }, [page, onPageChange]);

  const handleNextPage = useCallback(() => {
    if (page < totalPages && onPageChange) {
      onPageChange(page + 1);
    }
  }, [page, totalPages, onPageChange]);

  const handlePageClick = useCallback((targetPage) => {
    if (onPageChange && targetPage >= 1 && targetPage <= totalPages) {
      onPageChange(targetPage);
    }
  }, [onPageChange, totalPages]);

  // 페이지 번호 배열 생성 (일부만 표시)
  const pageNumbers = useMemo(() => {
    const delta = isMobile ? 1 : 2; // 현재 페이지 좌우로 보여줄 페이지 수 (모바일은 1)
    const range = [];
    const rangeWithDots = [];

    // 항상 표시할 페이지: 첫 페이지, 마지막 페이지, 현재 페이지 주변
    for (let i = 1; i <= totalPages; i++) {
      if (
        i === 1 || // 첫 페이지
        i === totalPages || // 마지막 페이지
        (i >= page - delta && i <= page + delta) // 현재 페이지 주변
      ) {
        range.push(i);
      }
    }

    // 생략 기호(...) 추가
    let prev = 0;
    for (const i of range) {
      if (prev) {
        if (i - prev === 2) {
          // 차이가 2면 중간 숫자 추가
          rangeWithDots.push(prev + 1);
        } else if (i - prev > 2) {
          // 차이가 2보다 크면 ... 추가
          rangeWithDots.push('...');
        }
      }
      rangeWithDots.push(i);
      prev = i;
    }

    return rangeWithDots;
  }, [totalPages, page, isMobile]);

  return (
    <div className="pagination-info">
      <div>
        총 {totalCount}개 항목 중 {startIndex} - {endIndex}
      </div>
      <div className="pagination">
        <button onClick={handlePrevPage} disabled={page === 1}>
          이전
        </button>
        {pageNumbers.map((pageNum, index) => (
          pageNum === '...' ? (
            <span key={`dots-${index}`} className="pagination-dots">...</span>
          ) : (
            <button
              key={pageNum}
              className={page === pageNum ? 'active' : ''}
              onClick={() => handlePageClick(pageNum)}
            >
              {pageNum}
            </button>
          )
        ))}
        <button onClick={handleNextPage} disabled={page >= totalPages}>
          다음
        </button>
      </div>
    </div>
  );
}

export default React.memo(CampaignPagination);

